import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { Entities, Entity } from '@polyai/schemas';
import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';

import {
  isNoCodeStepReferencesEntity,
  isStepReferencesEntity,
} from '../../matchers';
import { createReferenceAdapter } from '../../references';
import {
  handleCreateFlowReferences,
  handleDeleteFlowNoCodeStepReferences,
  handleDeleteFlowReferences,
  handleDeleteFlowStepReferences,
  handleDeleteNoCodeStepReferences,
  handleImportFlowReferences,
  handleNoCodeStepReferenceUpdates,
  handleStepReferenceUpdates,
} from '../../utils/reference-helpers';
import {
  createFlow,
  deleteFlow,
  deleteFlowStep,
  deleteNoCodeStep,
  deleteStep,
  importFlow,
} from '../flows/actions';
import { createEntity, deleteEntity, updateEntity } from './actions';
import type { EntityReferences } from './types';
import { convertProtoEntityTypeToEntityType } from './utils';

const entityAdapter = createEntityAdapter<Entity>();
const entitySelectors = entityAdapter.getSelectors();

const entityReferences = createReferenceAdapter(['flowSteps', 'noCodeSteps']);

const initialState = {
  entities: entityAdapter.getInitialState(),
} satisfies Entities;

const slice = createSlice({
  name: 'entities',
  initialState,
  reducers: {},
  // all reducers are here because actions are defined externally
  extraReducers: builder => {
    // main slice reducers
    builder.addCase(createEntity, (state, action) => {
      const entity: Entity = {
        ...action.payload,
        references:
          action.payload.references || entityReferences.createInitialState(),
        type: convertProtoEntityTypeToEntityType(action.payload.type),
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      };
      entityAdapter.addOne(state.entities, assignMetadata(action, entity));
    });

    builder.addCase(updateEntity, (state, action) => {
      const entity = entitySelectors.selectById(
        state.entities,
        action.payload.id,
      );

      if (entity) {
        const assignedFields = assignDefinedFields(
          { ...entity },
          {
            ...action.payload,
            type: action.payload.type
              ? convertProtoEntityTypeToEntityType(action.payload.type)
              : entity.type,
            references: action.payload.references
              ? {
                  ...entity.references,
                  ...action.payload.references,
                }
              : entity.references,
          },
        );

        assignedFields.config =
          action.payload.config !== undefined
            ? action.payload.config
            : entity.config;

        const changes = assignMetadata(action, assignedFields);

        entityAdapter.updateOne(state.entities, {
          id: action.payload.id,
          changes,
        });
      }
    });
    builder.addCase(deleteEntity, (state, action) => {
      const entity = entitySelectors.selectById(
        state.entities,
        action.payload.id,
      );
      if (entity) {
        entityAdapter.removeOne(state.entities, entity.id);
      }
    });

    // SIDE-EFFECT: FLOW
    builder.addCase(createFlow, (state, action) => {
      handleCreateFlowReferences<'noCodeSteps' | 'flowSteps'>(
        action.payload.steps,
        'entities',
        action.payload.id,
        entityReferences.addReference,
        state.entities,
      );
    });

    builder.addCase(importFlow, (state, action) => {
      if (action.payload.entities.length) {
        entityAdapter.addMany(
          state.entities,
          action.payload.entities.map(e => {
            const entity = {
              ...e,
              type: convertProtoEntityTypeToEntityType(e.type),
              createdAt: '',
              createdBy: '',
              updatedAt: '',
              updatedBy: '',
              config: e.config,
            };
            return assignMetadata(action, entity);
          }),
        );
      }
      handleImportFlowReferences<'noCodeSteps' | 'flowSteps'>(
        action.payload.steps,
        'entities',
        action.payload.flowId,
        entityReferences.addReference,
        state.entities,
      );
    });

    builder.addCase(deleteFlow, (state, action) => {
      // delete for flow steps
      handleDeleteFlowReferences<Entity, EntityReferences>(
        entitySelectors.selectAll(state.entities),
        action.payload.flowId,
        entityReferences.removeReferencePrefix,
        state.entities,
      );
      // delete for no flow code
      handleDeleteNoCodeStepReferences<Entity, EntityReferences>(
        entitySelectors.selectAll(state.entities),
        action.payload.flowId,
        entityReferences.removeReferencePrefix,
        state.entities,
      );
    });

    builder.addCase(deleteFlowStep, (state, action) => {
      handleDeleteFlowStepReferences<Entity, EntityReferences>(
        entitySelectors.selectAll(state.entities),
        action.payload.flowId,
        action.payload.stepId,
        entityReferences.removeReference,
        state.entities,
      );
    });

    builder.addCase(deleteNoCodeStep, (state, action) => {
      handleDeleteFlowNoCodeStepReferences(
        entitySelectors.selectAll(state.entities),
        action.payload.flowId,
        action.payload.stepId,
        entityReferences.removeReference,
        state.entities,
      );
    });

    builder.addCase(deleteStep, (state, action) => {
      handleDeleteFlowNoCodeStepReferences(
        entitySelectors.selectAll(state.entities),
        action.payload.flowId,
        action.payload.stepId,
        entityReferences.removeReference,
        state.entities,
      );
    });

    builder.addMatcher(isNoCodeStepReferencesEntity, (state, action) => {
      const promptEntities = action.payload.references?.entities ?? {};
      const extractedEntities =
        action.payload.references?.extractedEntities ?? {};

      handleNoCodeStepReferenceUpdates(
        state.entities.entities,
        {
          ...promptEntities,
          ...extractedEntities,
        },
        action.payload.flowId,
        action.payload.stepId,
        entityReferences.addReference,
        entityReferences.removeReference,
        state.entities,
      );
    });

    builder.addMatcher(isStepReferencesEntity, (state, action) => {
      handleStepReferenceUpdates(
        state.entities.entities,
        action.payload.step?.references?.entities ?? {},
        action.payload.flowId,
        action.payload.step.id,
        entityReferences.addReference,
        entityReferences.removeReference,
        state.entities,
      );
    });
  },
});

export { entityAdapter };

export { entitySelectors };

export { createEntity, deleteEntity, entityReferences, updateEntity };

export default slice.reducer;
